import React, { useState, useMemo, useEffect } from 'react';
import Select from "react-select";
import { useSelector, useDispatch } from "react-redux";
import { fetchTablesWitOtp, updateTableStatus, openGlobalPopup } from "../../redux/action";
import { Link } from "react-router-dom";
import { statusMap, reverseStatusMap } from "../../Utills/enums";
import GlobalPopup from "../../component/Popup/GlobalPopup.js";
import "./TableStatus.css";
import EmptyScreen from "../../component/common/EmptyScreen/index.js";
import { acknowledgeTableOrders } from "../../redux/notifications/notificationSlice";
import { advancedBillingApi } from "../../services/advancedBillingApi";
import { toast } from "../../Utills/toast";
import { useTranslation } from "react-i18next";
import { ButtonLoader, SectionLoader } from "../../component/common/Loader";

const statusColors = {
  Empty: "#4CAF50",
  Occupied: "#F44336",
  Maintenance: "#9E9E9E",
  BillingRequested: "#9C27B0",
};

const statuses = ["Empty", "Occupied", "Maintenance", "BillingRequested"];
const statusLabels = { BillingRequested: "Billing Requested" };
const statusOptions = [
  { value: "", label: "All Statuses" },
  ...statuses.map((status) => ({ value: status, label: statusLabels[status] || status })),
];

const normalizeTableReference = (value) => String(value ?? "")
  .trim()
  .toLowerCase()
  .replace(/^table\s*/, "")
  .replace(/^t-/, "");

const isMobileTableViewport = () => typeof window !== "undefined"
  && typeof window.matchMedia === "function"
  && window.matchMedia("(max-width: 700px)").matches;

export const getPendingTableOrders = (notifications = [], table = {}) => {
  const tableNumber = normalizeTableReference(table.tableName);
  const tableOtp = String(table.tableOpt ?? "").trim();

  return notifications.filter((notification) => {
    if (notification?.type !== "NEW_ORDER" || notification.acknowledged) return false;
    const matchesNumber = tableNumber && normalizeTableReference(notification.tableNumber) === tableNumber;
    const matchesOtp = tableOtp && String(notification.tableOtp ?? "").trim() === tableOtp;
    return Boolean(matchesNumber || matchesOtp);
  });
};

export const buildTableOperationPayload = ({ source, target, operation, items = [] }) => {
  const payload = {
    sourceTableNumber: source?.tableName,
    targetTableNumber: Number(target?.tableName),
    reason: operation.reason,
    expectedSourceTableOtp: source?.tableOpt,
    expectedTargetTableOtp: target?.tableOpt,
  };
  if (operation.type === "ITEM_TRANSFER") {
    payload.items = items.filter(item => item.selected).map(item => ({
      foodOrderId: item.foodOrderId,
      quantity: Number(item.transferQuantity),
      expectedQuantity: Number(item.quantity),
    }));
  }
  return payload;
};

const filterSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "43px",
    height: "43px",
    backgroundColor: "var(--ui-surface)",
    borderColor: state.isFocused ? "var(--ui-primary)" : "var(--ui-border)",
    boxShadow: state.isFocused ? "0 0 0 1px var(--ui-primary)" : "none",
    "&:hover": { borderColor: state.isFocused ? "var(--ui-primary)" : "var(--ui-border)" },
  }),
  menu: (base) => ({
    ...base,
    overflow: "hidden",
    backgroundColor: "var(--ui-surface)",
    border: "1px solid var(--ui-border)",
    boxShadow: "var(--ui-shadow-raised)",
  }),
  menuList: (base) => ({ ...base, padding: 0 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "var(--ui-primary)"
      : state.isFocused
        ? "var(--ui-surface-muted)"
        : "var(--ui-surface)",
    color: state.isSelected ? "var(--ui-on-primary)" : "var(--ui-text)",
    cursor: "pointer",
    ":active": { backgroundColor: "var(--ui-primary)", color: "var(--ui-on-primary)" },
  }),
  singleValue: (base) => ({ ...base, color: "var(--ui-text)" }),
  input: (base) => ({ ...base, color: "var(--ui-text)" }),
  placeholder: (base) => ({ ...base, color: "var(--ui-text-muted)" }),
  dropdownIndicator: (base) => ({ ...base, color: "var(--ui-text-muted)" }),
  indicatorSeparator: () => ({ display: "none" }),
};

function TableStatus({ tableData = [] }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const realtimeRevision = useSelector(state => state.notifications?.tablesRevision || 0);
  const notifications = useSelector(state => state.notifications?.list || []);
  const { tables, loading } = useSelector((state) => state.admin);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBoxIndex, setSelectedBoxIndex] = useState(null);
  const [hoveredTable, setHoveredTable] = useState(null);
  const [, setTick] = useState(0);
  const [tableOperation, setTableOperation] = useState({ type: "TRANSFER", targetTableNumber: "", reason: "" });
  const [transferItems, setTransferItems] = useState([]);
  const [operationBusy, setOperationBusy] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [operationHistory, setOperationHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [undoOperation, setUndoOperation] = useState({ id: null, reason: "" });
  const roleId = Number(JSON.parse(localStorage.getItem("admin_token_info") || "{}").roleId);

  // --- DROPDOWN STATES ---
  const [selectedStatus, setSelectedStatus] = useState(""); // Native Status State ("" matlab All)
  const [selectedTable, setSelectedTable] = useState({ value: "ALL", label: "All Tables" }); // React-Select State

  // 1. React-Select Table Dropdown Options
  const tableOptions = useMemo(() => {
    const baseOptions = [{ value: "ALL", label: "All Tables" }];
    if (!tables || !Array.isArray(tables)) return baseOptions;
    
    const dynamicOptions = tables.map((t) => ({
      value: t.restaurantTableId, 
      label: `Table ${t.tableName}`,
      raw: t 
    }));
    return [...baseOptions, ...dynamicOptions];
  }, [tables]);

  // 2. FIXED: Combined Filter Logic for Both Dropdowns
  const filteredTables = useMemo(() => {
    if (!tables || !Array.isArray(tables)) return [];

    return tables.filter((table) => {
      // Condition A: Table Dropdown Filter Check
      const matchesTable = !selectedTable || selectedTable.value === "ALL" || table.restaurantTableId === selectedTable.value;

      // Condition B: Status Dropdown Filter Check
      const currentTableStatusText = statusMap[table.tableStatus]; // Integer to String mapping (e.g., 1 -> "Empty")
      const matchesStatus = selectedStatus === "" || currentTableStatusText === selectedStatus;

      // Jab dono condition check paas hongi tabhi table card screen par dikhega
      return matchesTable && matchesStatus;
    });
  }, [tables, selectedTable, selectedStatus]);

  useEffect(() => {
    dispatch(fetchTablesWitOtp());
  }, [dispatch, realtimeRevision]);

  const handleBoxClick = (index, actualTableObj) => {
    // Redux state ke strict array index ko nikalne ke liye filtered object ko original index se match karein
    const originalIndex = tables.findIndex(t => t.restaurantTableId === actualTableObj.restaurantTableId);
    setSelectedBoxIndex(originalIndex !== -1 ? originalIndex : index);
    setIsModalOpen(true);
  };

  const handleStatusChange = (newStatus) => {
    const updatedBoxes = tables.map((box, index) =>
      index === selectedBoxIndex
        ? { ...box, tableStatus: reverseStatusMap[newStatus] }
        : box
    );

    const table = updatedBoxes[selectedBoxIndex];
    dispatch(updateTableStatus(table));
    setIsModalOpen(false);
    setSelectedBoxIndex(null);
  };

  const handleCloseModal = () => {
    if (operationBusy) return;
    setIsModalOpen(false);
    setSelectedBoxIndex(null);
    setTransferItems([]);
    setTableOperation({ type: "TRANSFER", targetTableNumber: "", reason: "" });
  };

  const loadTransferItems = async () => {
    const sourceTableNumber = tables[selectedBoxIndex]?.tableName;
    if (!sourceTableNumber) return;
    setItemsLoading(true);
    try {
      const response = await advancedBillingApi.getTableTransferItems(sourceTableNumber);
      setTransferItems((response?.items || []).map(item => ({ ...item, selected: false, transferQuantity: 1 })));
    } catch (requestError) {
      setTransferItems([]);
      toast.error(requestError.message);
    } finally { setItemsLoading(false); }
  };

  const changeOperationType = event => {
    const type = event.target.value;
    setTableOperation(value => ({ ...value, type, targetTableNumber: "" }));
    setTransferItems([]);
    if (type === "ITEM_TRANSFER") loadTransferItems();
  };

  const updateTransferItem = (foodOrderId, changes) => setTransferItems(items => items.map(item =>
    item.foodOrderId === foodOrderId ? { ...item, ...changes } : item));

  const loadOperationHistory = async () => {
    setHistoryLoading(true);
    try { setOperationHistory(await advancedBillingApi.getTableOperationHistory()); }
    catch (requestError) { toast.error(requestError.message); }
    finally { setHistoryLoading(false); }
  };

  const openOperationHistory = () => {
    setUndoOperation({ id: null, reason: "" });
    dispatch(openGlobalPopup("table-operation-history"));
    loadOperationHistory();
  };

  const undoMerge = async operationId => {
    if (!undoOperation.reason.trim()) return;
    setOperationBusy(true);
    try {
      await advancedBillingApi.undoTableOperation(operationId, undoOperation.reason.trim());
      toast.success(t("Table merge undone successfully"));
      setUndoOperation({ id: null, reason: "" });
      await loadOperationHistory();
      dispatch(fetchTablesWitOtp());
    } catch (requestError) { toast.error(requestError.message); }
    finally { setOperationBusy(false); }
  };

  const submitTableOperation = async (event) => {
    event.preventDefault();
    const target = tables.find(table => Number(table.tableName) === Number(tableOperation.targetTableNumber));
    const source = tables[selectedBoxIndex];
    const payload = buildTableOperationPayload({ source, target, operation: tableOperation, items: transferItems });
    if (tableOperation.type === "ITEM_TRANSFER") {
      payload.items = transferItems.filter(item => item.selected).map(item => ({ foodOrderId: item.foodOrderId,
        quantity: Number(item.transferQuantity), expectedQuantity: Number(item.quantity) }));
      if (!payload.items.length) { toast.error(t("Select at least one item to transfer")); return; }
    }
    setOperationBusy(true);
    try {
      if (tableOperation.type === "MERGE") await advancedBillingApi.mergeTable(payload);
      else if (tableOperation.type === "ITEM_TRANSFER") await advancedBillingApi.transferTableItems(payload);
      else await advancedBillingApi.transferTable(payload);
      toast.success(t(tableOperation.type === "MERGE" ? "Table merged successfully" : "Table transfer completed successfully"));
      setIsModalOpen(false);
      setSelectedBoxIndex(null);
      setTransferItems([]);
      setTableOperation({ type: "TRANSFER", targetTableNumber: "", reason: "" });
      dispatch(fetchTablesWitOtp());
    } catch (operationError) { toast.error(operationError.message); }
    finally { setOperationBusy(false); }
  };

  const getReservationStatus = (date, time) => {
    const now = new Date();
    const onlyDate = date.split("T")[0];
    const resTime = new Date(`${onlyDate}T${time}`);
    const diff = (resTime - now) / (1000 * 60);

    if (diff > 30) return "green";
    if (diff > 0) return "yellow";
    return "red";
  };

  const getTimeLeft = (date, time) => {
    if (!date || !time) return "";
    const resDate = date.split("T")[0];
    const resTime = new Date(`${resDate}T${time}`);
    const now = new Date();
    const diff = resTime - now;

    if (diff <= 0) return "Started";
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setTick(prev => prev + 1);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="Table-screen-container">
      <h1>Table Screen</h1>

      <div className="filter-row">
        <div className="filter-controls">
          <Select
            inputId="table-status-filter"
            className="table-select-react"
            classNamePrefix="table-select"
            options={statusOptions}
            value={statusOptions.find((option) => option.value === selectedStatus)}
            onChange={(option) => setSelectedStatus(option?.value || "")}
            isSearchable={false}
            menuPortalTarget={document.body}
            styles={filterSelectStyles}
          />

          <Select
            inputId="table-filter"
            className="table-select-react"
            classNamePrefix="table-select"
            options={tableOptions}
            value={selectedTable}
            onChange={setSelectedTable}
            placeholder="Search or select table..."
            isSearchable={true}
            isLoading={loading}
            menuPortalTarget={document.body}
            styles={filterSelectStyles}
          />
          {[1, 2].includes(roleId) && <button type="button" className="table-history-button" onClick={openOperationHistory}>
            {t("Operation history")}
          </button>}
        </div>

        <div className="legend-row">
          {statuses.map((status) => (
            <div key={status} className="legend-item">
              <span
                className="status-dot"
                style={{ backgroundColor: statusColors[status] }}
              ></span>
              {status}
            </div>
          ))}
          <div className="legend-item">
            <span className="status-dot status-dot--new-order"></span>
            New Order
          </div>
        </div>
      </div>

      {/* FIXED: Render using filteredTables array instead of raw tables */}
      {loading ? (
        <SectionLoader label={t("Loading tables...")} rows={4} />
      ) : filteredTables.length !== 0 ? (
        <div className="boxes-grid">
          {filteredTables.map((box, index) => {
            const pendingOrders = getPendingTableOrders(notifications, box);
            const hasNewOrder = pendingOrders.length > 0;
            const tableStatus = statusMap[box.tableStatus];
            const tableStatusColor = statusColors[tableStatus];

            return (
            <div
              key={box.restaurantTableId || index}
              className={`box${hasNewOrder ? " box--new-order" : ""}`}
              style={{ backgroundColor: tableStatusColor, "--table-status-color": tableStatusColor }}
              onClick={() => {
                if (!isMobileTableViewport()) handleBoxClick(index, box);
              }}
            >
              <div className="table-card-topline">
                <span>T-{box.tableName}</span>
                <span className="table-card-status">
                  <span aria-hidden="true"></span>
                  {statusLabels[tableStatus] || tableStatus}
                </span>
                {hasNewOrder && (
                  <span className="new-order-badge" aria-label={`${pendingOrders.length} new order${pendingOrders.length === 1 ? "" : "s"}`}>
                    <span className="new-order-badge__dot" aria-hidden="true"></span>
                    NEW{pendingOrders.length > 1 ? ` ${pendingOrders.length}` : ""}
                  </span>
                )}
              </div>

              {box.reservationId != null && (
                <span className="reservation-countdown">⏰ {box.reservationTime?.slice(0, 5)}</span>
              )}

              <span className="reservation-countdown">Code: {box.tableOpt}</span>

              {box.reservationId != null && (
                <span className="reservation-countdown reservation-badge">
                  ⏱ {getTimeLeft(box.reservationDate, box.reservationTime)}
                </span>
              )}
              
              <div className={`table-card-actions${box.reservations?.length > 0 ? " table-header has-reservations" : ""}`}>
                <Link 
                  className="view table-card-open-action"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (hasNewOrder) {
                      dispatch(acknowledgeTableOrders({ tableNumber: box.tableName, tableOtp: box.tableOpt }));
                    }
                  }}
                  to={`/table/T-${box.tableName}/${box.tableOpt}`}
                >
                  <span className="table-action-label--desktop">View</span>
                  <span className="table-action-label--mobile">Open</span>
                </Link>
                {box?.reservations?.length > 0 && (
                  <span
                    className="view table-card-reservation-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      setHoveredTable(box);
                      setIsModalOpen(false);
                      dispatch(openGlobalPopup("view-reservations"));
                    }}
                  >
                    <span className="table-action-label--desktop">📅 {box.reservations.length}</span>
                    <span className="table-action-label--mobile">📅 {box.reservations.length}</span>
                  </span>
                )}
                <Link
                  type="button"
                  className="view table-card-more-action"
                  aria-label={`More actions for table T-${box.tableName}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleBoxClick(index, box);
                  }}
                >
                  More
                </Link>
              </div>
            </div>
            );
          })}
        </div>
      ) : (
        <EmptyScreen
          message={tables?.length ? "No tables match the selected filters." : "No tables available yet."}
          subText={tables?.length ? "Clear the filters to view all available tables." : "Add a table from Settings to begin taking dine-in orders."}
          actionLabel={tables?.length ? "Clear filters" : undefined}
          onAction={tables?.length ? () => {
            setSelectedStatus("");
            setSelectedTable({ value: "ALL", label: "All Tables" });
          } : undefined}
        />
      )}

      {/* Reservation Popup */}
      {hoveredTable && (
        <GlobalPopup popupKey="view-reservations" title={`Table T-${hoveredTable.tableName} Reservations`}>
          <div className="reservation-popup">
            <div className="reservation-list">
              {hoveredTable?.reservations?.map((res) => {
                const status = getReservationStatus(res.reservationDate, res.reservationTime);
                return (
                  <div key={res.reservationId} className={`reservation-card ${status}`}>
                    <div className="res-time">⏰ {res.reservationTime}</div>
                    <div className="res-info">
                      <div className="res-name">{res.customerName}</div>
                      <div className="res-mobile">📞 {res.mobileNumber}</div>
                    </div>
                    <div className="res-guests">👥 {res.numberOfGuests}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </GlobalPopup>
      )}

      <GlobalPopup
        popupKey="table-operation-history"
        title={t("Table operation history")}
        disableClose={operationBusy}
        footer={closePopup => <button type="button" className="popup-action-btn secondary" onClick={closePopup} disabled={operationBusy}>{t("Close")}</button>}
      >
        <div className="table-operation-history">
          {historyLoading && <div className="table-operation-loading"><span className="popup-button-spinner" />{t("Loading history...")}</div>}
          {!historyLoading && operationHistory.length === 0 && <p className="table-operation-empty">{t("No table operations recorded yet.")}</p>}
          {!historyLoading && operationHistory.map(operation => {
            const movedItems = operation.details?.Items || operation.details?.items || [];
            const isUndoOpen = undoOperation.id === operation.tableOperationLogId;
            return <article className="table-operation-history-card" key={operation.tableOperationLogId}>
              <div className="table-operation-history-head">
                <div><strong>{t(operation.operationType === "ITEM_TRANSFER" ? "Item transfer" : operation.operationType === "MERGE" ? "Table merge" : "Table transfer")}</strong>
                  <span>T-{operation.sourceTableNumber} → T-{operation.targetTableNumber}</span></div>
                <span className={`table-operation-status ${operation.operationStatus?.toLowerCase()}`}>{t(operation.operationStatus === "UNDONE" ? "Undone" : "Completed")}</span>
              </div>
              <p>{operation.reason}</p>
              {movedItems.length > 0 && <div className="table-operation-items-summary">
                {movedItems.map((item, index) => <span key={`${item.SourceFoodOrderId || item.sourceFoodOrderId}-${index}`}>
                  {item.ItemName || item.itemName} × {item.MovedQuantity || item.movedQuantity}
                </span>)}
              </div>}
              <div className="table-operation-meta">
                <span>{operation.performedBy}</span><span>{new Date(operation.createdAt).toLocaleString()}</span>
              </div>
              {operation.operationStatus === "UNDONE" && <div className="table-operation-undo-note">
                {t("Undone by")} {operation.undoneBy} · {operation.undoReason}
              </div>}
              {operation.canUndo && !isUndoOpen && <button type="button" className="table-operation-undo-link" onClick={() => setUndoOperation({ id: operation.tableOperationLogId, reason: "" })}>
                {t("Undo merge")}
              </button>}
              {operation.canUndo && isUndoOpen && <div className="table-operation-undo-form">
                <input autoFocus value={undoOperation.reason} disabled={operationBusy} placeholder={t("Reason for undo")}
                  onChange={event => setUndoOperation(value => ({ ...value, reason: event.target.value }))} />
                <button type="button" className="secondary" disabled={operationBusy} onClick={() => setUndoOperation({ id: null, reason: "" })}>{t("Cancel")}</button>
                <button type="button" disabled={operationBusy || !undoOperation.reason.trim()} onClick={() => undoMerge(operation.tableOperationLogId)}>
                  {operationBusy && <span className="popup-button-spinner" />}{operationBusy ? t("Undoing...") : t("Confirm undo")}
                </button>
              </div>}
            </article>;
          })}
        </div>
      </GlobalPopup>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay table-status-modal-overlay">
          <div className="modal-content table-status-modal-content">
            <h2>Change Status for {tables[selectedBoxIndex]?.tableName}</h2>
            <div className="status-options table-status-options">
              {statuses.map((status) => (
                <button
                  key={status}
                  style={{ backgroundColor: statusColors[status] }}
                  onClick={() => handleStatusChange(status)}
                  aria-pressed={tables[selectedBoxIndex]?.tableStatus === reverseStatusMap[status]}
                >
                  {statusLabels[status] || status}
                </button>
              ))}
            </div>
            {[1, 2].includes(roleId) && tables[selectedBoxIndex]?.tableStatus !== 0 && <form className="table-operation-form" onSubmit={submitTableOperation}>
              <h3>{t("Merge or transfer table")}</h3>
              <select aria-label="Table operation" value={tableOperation.type} onChange={changeOperationType} disabled={operationBusy}>
                <option value="TRANSFER">{t("Transfer complete table")}</option>
                <option value="MERGE">{t("Merge into occupied table")}</option>
                <option value="ITEM_TRANSFER">{t("Transfer selected items")}</option>
              </select>
              <select required aria-label="Target table" value={tableOperation.targetTableNumber} disabled={operationBusy} onChange={event => setTableOperation(value => ({ ...value, targetTableNumber: event.target.value }))}>
                <option value="">{t("Select target table")}</option>
                {tables.filter(table => table.tableName !== tables[selectedBoxIndex]?.tableName &&
                  (tableOperation.type === "MERGE" ? table.tableStatus === 1 : tableOperation.type === "ITEM_TRANSFER" ? [0, 1].includes(table.tableStatus) : table.tableStatus === 0))
                  .map(table => <option key={table.restaurantTableId} value={table.tableName}>T-{table.tableName} · {t(statusMap[table.tableStatus])}</option>)}
              </select>
              {tableOperation.type === "ITEM_TRANSFER" && <div className="table-transfer-items">
                {itemsLoading && <div className="table-operation-loading"><span className="popup-button-spinner" />{t("Loading items...")}</div>}
                {!itemsLoading && transferItems.length === 0 && <p>{t("No transferable items found.")}</p>}
                {!itemsLoading && transferItems.map(item => <label className={`table-transfer-item ${item.selected ? "selected" : ""}`} key={item.foodOrderId}>
                  <input type="checkbox" checked={item.selected} disabled={operationBusy} onChange={event => updateTransferItem(item.foodOrderId, { selected: event.target.checked })} />
                  <span><strong>{item.name}</strong><small>{t("Available quantity")}: {item.quantity}{item.addonSummary ? ` · ${item.addonSummary}` : ""}</small></span>
                  <input type="number" aria-label={`${item.name} quantity`} min="1" max={item.quantity} value={item.transferQuantity} disabled={!item.selected || operationBusy}
                    onChange={event => updateTransferItem(item.foodOrderId, { transferQuantity: Math.min(item.quantity, Math.max(1, Number(event.target.value) || 1)) })} />
                </label>)}
              </div>}
              <input required value={tableOperation.reason} disabled={operationBusy} placeholder={t("Reason")} onChange={event => setTableOperation(value => ({ ...value, reason: event.target.value }))} />
              <button type="submit" disabled={operationBusy || itemsLoading}>{operationBusy && <span className="popup-button-spinner" />}
                {operationBusy ? <ButtonLoader label={t("Processing...")} /> : t(tableOperation.type === "MERGE" ? "Merge table" : tableOperation.type === "ITEM_TRANSFER" ? "Transfer items" : "Transfer table")}
              </button>
            </form>}
            <button className="close-modal-button table-status-close-button" disabled={operationBusy} onClick={handleCloseModal}>
              {t("Close")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TableStatus;


